import React, { useState, useEffect } from "react";
import ApiClient from "../api/ApiClient";

const pageSizeOptions = [10, 25, 100, 500];

export const Tickers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("");
  const [collectCandles, setCollectCandles] = useState("");
  const [tag, setTag] = useState("");
  const [tickerType, setTickerType] = useState("");
  const [source, setSource] = useState("");
  const [packageTickers, setPackageTickers] = useState("");

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0); // zero-based

  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sectorsMap, setSectorsMap] = useState({});
  const [industriesMap, setIndustriesMap] = useState({});
  const [countriesMap, setCountriesMap] = useState({});
  const [exchangesMap, setExchangesMap] = useState({});
  const [tickerTypes, setTickerTypes] = useState([]);
  const [sources, setSources] = useState([]);

  // Modal state
  const [editingTicker, setEditingTicker] = useState(null);
  const [editCollectCandles, setEditCollectCandles] = useState(false);
  const [editActive, setEditActive] = useState(false);
  const [editTags, setEditTags] = useState("");
  const [editCountry, setEditCountry] = useState("");
  const [editCountryDirty, setEditCountryDirty] = useState(false);
  const [editCountrySaving, setEditCountrySaving] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    // Fetch ticker types on mount
    ApiClient.getTickerTypes()
      .then(types => setTickerTypes(types))
      .catch(err => console.error('Failed to fetch ticker types:', err));
    ApiClient.getSources()
      .then(srcs => setSources(srcs))
      .catch(err => console.error('Failed to fetch sources:', err));
  }, []);

  async function fetchData() {
    setLoading(true);
    const body = {
      SearchTerm: searchTerm || undefined,
      Code: code || undefined,
      NamePartial: name || undefined,
      Currency: currency || undefined,
      CollectCandles:
        collectCandles === "" ? undefined : collectCandles === "true",
      Tag: tag || undefined,
      Types: tickerType ? [tickerType] : undefined,
      Source: source || undefined,
      PackageTickers: packageTickers === "" ? undefined : packageTickers === "true",
      ExtraFields: "StaticDetails",
      Limit: pageSize,
      Offset: page * pageSize,
    };

    try {
      const data = await ApiClient.searchTickers(body);
      setItems(data.Items);
      setTotal(data.TotalCount);
      // load reference maps in background (cached in ApiClient)
      ApiClient.getSectorsMap().then((m) => setSectorsMap(m)).catch(() => {});
      ApiClient.getIndustriesMap().then((m) => setIndustriesMap(m)).catch(() => {});
      ApiClient.getCountriesMap().then((m) => setCountriesMap(m)).catch(() => {});
      ApiClient.getExchangesMap().then((m) => setExchangesMap(m)).catch(() => {});
    } catch (err) {
      console.error("Search failed", err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(0);
    fetchData();
  }

  async function saveEditCountry() {
    if (!editingTicker) return;
    const name = (editCountry || "").trim();
    if (!name) return;

    setEditCountrySaving(true);
    try {
      const updated = await ApiClient.updateTickerCountry(editingTicker.Id, { Name: name });
      setItems((prev) => prev.map((it) => (it.Id === updated.Id ? updated : it)));
      // Refresh countries map in the background so the new id -> name mapping surfaces
      // immediately in the table cell; the next search also re-hydrates the map.
      ApiClient.getCountriesMap()
        .then((m) => setCountriesMap(m))
        .catch(() => {});
      setEditingTicker(updated);
      setEditCountry(updated.CountryId ? countriesMap[updated.CountryId] || name : name);
      setEditCountryDirty(false);
    } catch (err) {
      console.error("Country update failed", err);
      alert("Country update failed: " + (err.message || err));
    } finally {
      setEditCountrySaving(false);
    }
  }

  function renderTable() {
    if (loading)
      return (
        <p>
          <em>Loading...</em>
        </p>
      );
    if (!items || items.length === 0) return <p>No results</p>;

    const modalTitleName = editingTicker ? editingTicker.Name : "";

    return (
      <>
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Id</th>
              <th>Source</th>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th>Sector</th>
              <th>Industry</th>
              <th>Country</th>
              <th>Exchange</th>
              <th>Currency</th>
              <th>Collect candles</th>
              <th>Tags</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => {
              return (
                <tr
                  key={t.Id}
                  style={{ cursor: "pointer" }}
                  onClick={() => openEditModal(t)}
                >
                  <td nowrap="true">
                    {t.Id}
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 ms-1 align-baseline"
                      title={`Copy ticker id ${t.Id} to clipboard`}
                      aria-label={`Copy ticker id ${t.Id} to clipboard`}
                      onClick={(e) => {
                        e.stopPropagation();
                        copyTickerId(t.Id);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M4 1.5A1.5 1.5 0 0 1 5.5 0h5A1.5 1.5 0 0 1 12 1.5v1H5.5a1.5 1.5 0 0 0-1.5 1.5v6.5H3A1.5 1.5 0 0 1 1.5 9V3A1.5 1.5 0 0 1 3 1.5h1V2H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h1V9a2 2 0 0 1 2-2h6V6h1V4a1 1 0 0 0-1-1h-1V2a1 1 0 0 0-1-1H5.5a1 1 0 0 0-1 1v.5z" />
                        <path d="M5.5 3h6A1.5 1.5 0 0 1 13 4.5v9A1.5 1.5 0 0 1 11.5 15h-6A1.5 1.5 0 0 1 4 13.5v-9A1.5 1.5 0 0 1 5.5 3zm0 1a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h6a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-6z" />
                      </svg>
                    </button>
                  </td>
                  <td>{t.Source || ""}</td>
                  <td>{t.Code}</td>
                  <td>{t.Name}</td>
                  <td>{t.Type || ""}</td>
                  <td>{t.SectorId ? sectorsMap[t.SectorId] || "" : ""}</td>
                  <td>{t.IndustryId ? industriesMap[t.IndustryId] || "" : ""}</td>
                  <td>{t.CountryId ? countriesMap[t.CountryId] || "" : ""}</td>
                  <td>{t.ExchangeId ? exchangesMap[t.ExchangeId] || "" : ""}</td>
                  <td>{t.Currency}</td>
                  <td>{String(t.CollectCandles)}</td>
                  <td>{Array.isArray(t.Tags) ? t.Tags.join(", ") : ""}</td>
                  <td>{String(t.Active)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <a href={`/candles/ticker/${t.Id}`}>Show candles</a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Edit modal */}
        {editingTicker && (
          <div
            className="modal show"
            tabIndex="-1"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Edit Ticker #{editingTicker.Id}
                    {modalTitleName ? " - " + modalTitleName : ""}
                  </h5>
                </div>
                <div className="modal-body">
                  <div className="form-group form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="editCollectCandles"
                      checked={Boolean(editCollectCandles)}
                      onChange={(e) => setEditCollectCandles(e.target.checked)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="editCollectCandles"
                    >
                      Collect candles
                    </label>
                  </div>
                  <div className="form-group form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="editActive"
                      checked={Boolean(editActive)}
                      onChange={(e) => setEditActive(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="editActive">
                      Active
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Tags (comma separated)</label>
                    <input
                      className="form-control"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="editCountry">
                      Country
                      <span className="text-muted ml-2" style={{ fontSize: "0.85em" }}>
                        — type a country name; a new one is created if missing
                      </span>
                    </label>
                    <div className="d-flex" style={{ gap: "8px" }}>
                      <input
                        id="editCountry"
                        className="form-control"
                        placeholder="e.g. United States"
                        value={editCountry}
                        onChange={(e) => {
                          setEditCountry(e.target.value);
                          setEditCountryDirty(true);
                        }}
                      />
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={saveEditCountry}
                        disabled={editCountrySaving || !editCountryDirty || !editCountry.trim()}
                        title="Assign / re-assign country by name"
                      >
                        {editCountrySaving ? "Applying..." : "Apply"}
                      </button>
                    </div>
                    {editingTicker && editingTicker.CountryId
                      ? countriesMap[editingTicker.CountryId]
                        ? null
                        : (
                          <small className="text-warning d-block mt-1">
                            Currently linked to country Id {editingTicker.CountryId}, not in cached list.
                          </small>
                        )
                      : null}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={closeEditModal}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveEdit}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  async function copyTickerId(id) {
    const value = String(id);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    } catch (exc) {
      console.error("Failed to copy ticker id", exc);
      alert("Failed to copy ticker id: " + (exc.message || exc));
    }
  }

  function openEditModal(t) {
    setEditingTicker(t);
    setEditCollectCandles(Boolean(t.CollectCandles));
    setEditActive(Boolean(t.Active));
    setEditTags(Array.isArray(t.Tags) ? t.Tags.join(", ") : t.Tags || "");
    const currentCountry = t.CountryId ? countriesMap[t.CountryId] || "" : "";
    setEditCountry(currentCountry);
    setEditCountryDirty(false);
    setEditCountrySaving(false);
  }

  function closeEditModal() {
    setEditingTicker(null);
    setSaving(false);
    setEditCountry("");
    setEditCountryDirty(false);
    setEditCountrySaving(false);
  }

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape' && editingTicker) {
        closeEditModal();
      }
    }

    if (editingTicker) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [editingTicker]);

  async function saveEdit() {
    if (!editingTicker) return;
    const body = {
      Id: editingTicker.Id,
      CollectCandles: editCollectCandles,
      Active: editActive,
      Tags: editTags
        ? editTags
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        : [],
    };

    setSaving(true);
    try {
      const updated = await ApiClient.updateTicker(body);
      // Merge updated into items array in-place
      setItems((prev) => prev.map((it) => (it.Id === updated.Id ? updated : it)));
      closeEditModal();
    } catch (err) {
      console.error("Update failed", err);
      alert("Update failed: " + (err.message || err));
      setSaving(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <h1>Tickers</h1>
      <form onSubmit={onSearch} className="mb-3">
        <div style={{ maxWidth: "900px" }}>
          <div className="row">
            <div className="col-md-12 mb-2">
              <label className="col-form-label">Search Term</label>
              <input
                placeholder="Search by name or code (or #id)"
                className="form-control"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Code</label>
              <input
                placeholder="Code"
                className="form-control"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Name (partial)</label>
              <input
                placeholder="Name (partial)"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Currency</label>
              <input
                placeholder="Currency"
                className="form-control"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Collect candles</label>
              <select
                className="form-control"
                value={collectCandles}
                onChange={(e) => setCollectCandles(e.target.value)}
              >
                <option value="">&lt;Any&gt;</option>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Tag</label>
              <input
                placeholder="Tag"
                className="form-control"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Type</label>
              <select
                className="form-control"
                value={tickerType}
                onChange={(e) => setTickerType(e.target.value)}
              >
                <option value="">&lt;Any&gt;</option>
                {tickerTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Source</label>
              <select
                className="form-control"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <option value="">&lt;Any&gt;</option>
                {sources.map((src) => (
                  <option key={src} value={src}>
                    {src}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Package Tickers</label>
              <select
                className="form-control"
                value={packageTickers}
                onChange={(e) => setPackageTickers(e.target.value)}
              >
                <option value="">&lt;Any&gt;</option>
                <option value="true">In PackageTickers</option>
                <option value="false">Not in PackageTickers</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 mb-2 d-flex align-items-end">
              <button className="btn btn-primary" type="submit">
                Search
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="mb-2 d-flex align-items-center">
        <div className="mr-3">Page size:</div>
        <select
          className="form-control mr-3"
          style={{ width: "120px" }}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div>
          Page: {page + 1} / {totalPages || 1}
        </div>
      </div>

      <div className="mb-3 d-flex" style={{ gap: "8px", alignItems: "center" }}>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage(0)}
        >
          First
        </button>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage(totalPages - 1)}
        >
          Last
        </button>
      </div>

      {renderTable()}

      <div className="mt-2 d-flex" style={{ gap: "8px" }}>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage(0)}
        >
          First
        </button>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage(totalPages - 1)}
        >
          Last
        </button>
      </div>
    </div>
  );
};

export default Tickers;
